import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Autocomplete from '@mui/material/Autocomplete';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { isOptionEqualToValue } from '../utils';
import { useGet1090ProductsDesignationsQuery } from '../batchesEndpoints';
import palette from '../../../../library/theme/palette';

const Products1090 = ({ batchState, updateAndResetShipments }) => {
  const { product, fuelType, oxygenate, rvp, component, sulphurContent } = batchState;

  const isProductGasoline = R.propEq('isGasoline', true, product);

  const {
    data: productDesignations = {
      distillateOptions: { fuelType: [], sulphurContent: [] },
      gasolineOptions: { component: [], fuelType: [], oxygenate: [], rvp: [] },
    },
    isFetching: isFetchingDesignations,
    isSuccess: isDesignationsSuccess,
  } = useGet1090ProductsDesignationsQuery({ params: { isGasoline: isProductGasoline } });

  const isFuelTypeSummerCbobOrCg = R.anyPass([
    R.propEq('data', 'Summer CBOB'),
    R.propEq('data', 'Summer CG'),
    R.propEq('data', 'Summer RBOB'),
    R.propEq('data', 'Summer RFG'),
  ])(fuelType);

  const fuelTypeEnabled = isDesignationsSuccess;
  const oxygenateEnabled = isProductGasoline;
  const rvpEnabled = R.and(
    R.and(isDesignationsSuccess, isProductGasoline),
    isFuelTypeSummerCbobOrCg
  );
  const isJetFuel = R.propEq('data', 'Jet Fuel', fuelType);
  const isBlendstock = R.propEq('data', 'Gasoline Blendstock', fuelType);

  const getFilteredComponents = (components, selectedFuelType) => {
    if (R.propEq('data', 'Gasoline Blendstock', selectedFuelType)) {
      return (components || [])
        .filter((_, index) => index !== 1)
        .map(item => ({
          data: item,
          display: item,
        }));
    }
    return (components || []).map(item => ({
      data: item,
      display: item,
    }));
  };
  const isFuelForExport = R.propEq('data', 'Fuel For Export', fuelType);

  const handleFuelTypeChange = (_, value) => {
    if (R.propEq('data', 'Jet Fuel', value) || R.propEq('data', 'Fuel For Export', value)) {
      updateAndResetShipments(
        R.mergeLeft({
          component: { data: 'N/A', display: 'N/A' },
          fuelType: value,
          oxygenate: 'N/A',
          rvp: { data: 'N/A', display: 'N/A' },
          sulphurContent: { data: 'N/A', display: 'N/A' },
        })
      );
    } else if (R.propEq('data', 'Gasoline Blendstock', value)) {
      updateAndResetShipments(
        R.mergeLeft({
          component: null,
          fuelType: value,
          oxygenate: 'N/A',
          rvp: { data: 'N/A', display: 'N/A' },
          sulphurContent: { data: 'N/A', display: 'N/A' },
        })
      );
    } else {
      updateAndResetShipments(
        R.mergeLeft({
          component: null,
          fuelType: value,
          oxygenate: null,
          rvp: null,
          sulphurContent: null,
        })
      );
    }
  };
  const handleComponentChange = (_, value) => {
    updateAndResetShipments(R.assoc('component', value));
  };

  const handleSulphurContentChange = (_, value) => {
    updateAndResetShipments(R.assoc('sulphurContent', value));
  };

  const handleOxygenateChange = (_, value) => {
    updateAndResetShipments(R.assoc('oxygenate', value?.data));
  };

  const handleRvpChange = (_, value) => {
    updateAndResetShipments(R.assoc('rvp', value));
  };

  return (
    <>
      <Typography sx={{ color: palette.text.primary }} variant="label">
        1090 Product Designation{' '}
      </Typography>
      <Stack spacing={2}>
        <Typography sx={{ color: palette.text.secondary }} variant="body2">
          Due to regulations, you must document all transfers of gasoline, its additives, or
          regulated blendstocks, except for retail sales to end consumers.{' '}
        </Typography>
        <Autocomplete
          disableClearable
          disabled={!fuelTypeEnabled}
          getOptionLabel={R.prop('display')}
          isOptionEqualToValue={isOptionEqualToValue('data')}
          key="fuel-type"
          loading={isFetchingDesignations}
          onChange={handleFuelTypeChange}
          options={
            isProductGasoline
              ? (productDesignations?.gasolineOptions?.fuelType || []).map(item => ({
                  data: item,
                  display: item,
                }))
              : (productDesignations?.distillateOptions?.fuelType || []).map(item => ({
                  data: item,
                  display: item,
                }))
          }
          renderInput={inputParams => (
            <TextField {...inputParams} id="fuel-type" label="Fuel Type" />
          )}
          value={fuelType || null}
        />

        <Autocomplete
          disableClearable
          disabled={!oxygenateEnabled || isBlendstock || isFuelForExport}
          getOptionLabel={R.prop('display')}
          hidden={!isProductGasoline}
          isOptionEqualToValue={isOptionEqualToValue('data')}
          key="oxygenate-type"
          onChange={handleOxygenateChange}
          options={
            isProductGasoline
              ? (productDesignations?.gasolineOptions?.oxygenate || []).map(item => ({
                  data: item,
                  display: item,
                }))
              : []
          }
          renderInput={inputParams => (
            <TextField {...inputParams} id="oxygenate-type" label="Oxygenate Type and Amount" />
          )}
          value={oxygenate ? { data: oxygenate, display: oxygenate } : null}
        />

        <Autocomplete
          disableClearable
          disabled={!rvpEnabled || isBlendstock || isFuelForExport}
          getOptionLabel={R.prop('display')}
          hidden={!isProductGasoline}
          isOptionEqualToValue={isOptionEqualToValue('data')}
          key="rvp"
          loading={isFetchingDesignations}
          onChange={handleRvpChange}
          options={
            isProductGasoline
              ? (productDesignations?.gasolineOptions?.rvp || []).map(item => ({
                  data: item,
                  display: item,
                }))
              : []
          }
          renderInput={inputParams => <TextField {...inputParams} id="rvp" label="RVP" />}
          value={rvp || null}
        />

        <Autocomplete
          disableClearable
          disabled={isFuelForExport}
          getOptionLabel={R.prop('display')}
          hidden={!isProductGasoline}
          isOptionEqualToValue={isOptionEqualToValue('data')}
          key="component"
          loading={isFetchingDesignations}
          onChange={handleComponentChange}
          options={
            isProductGasoline
              ? getFilteredComponents(productDesignations?.gasolineOptions?.component, fuelType)
              : []
          }
          renderInput={inputParams => (
            <TextField {...inputParams} id="component" label="Component" />
          )}
          value={component}
        />

        <Autocomplete
          disableClearable
          disabled={isJetFuel || isFuelForExport}
          getOptionLabel={R.prop('display')}
          hidden={isProductGasoline}
          isOptionEqualToValue={isOptionEqualToValue('data')}
          key="sulphur-content"
          loading={isFetchingDesignations}
          onChange={handleSulphurContentChange}
          options={
            !isProductGasoline
              ? (productDesignations?.distillateOptions?.sulphurContent || []).map(item => ({
                  data: item,
                  display: item,
                }))
              : []
          }
          renderInput={inputParams => (
            <TextField {...inputParams} id="sulphur-content" label="Sulfur Content" />
          )}
          value={sulphurContent || null}
        />
      </Stack>
    </>
  );
};

Products1090.propTypes = {
  batchState: PropTypes.shape({
    component: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        data: PropTypes.string,
        display: PropTypes.string,
      }),
    ]),
    fuelType: PropTypes.shape({
      data: PropTypes.string,
      display: PropTypes.string,
    }),
    oxygenate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        data: PropTypes.string,
        display: PropTypes.string,
      }),
    ]),
    product: PropTypes.shape({
      isGasoline: PropTypes.bool,
    }),
    rvp: PropTypes.shape({
      data: PropTypes.string,
      display: PropTypes.string,
    }),
    sulphurContent: PropTypes.shape({
      data: PropTypes.string,
      display: PropTypes.string,
    }),
  }).isRequired,
  updateAndResetShipments: PropTypes.func.isRequired,
};

export default Products1090;
